import { Modal } from "antd";
import { useState } from "react";
import FormInput from "./FormInput";
import { SEND_OTP, VERIFY_OTP } from "src/graphql/mutations/auth";
import { useMutation } from "urql";

interface CreditLoginModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: (token: string, credits: number, authType?: string | null) => void;
  userEmail: string;
  userCredits: number;
  authType?: "email" | "google-oauth2" | null;
}

export const CreditLoginModal = ({
  visible,
  onClose,
  onSuccess,
  userEmail,
  userCredits,
  authType,
}: CreditLoginModalProps) => {
  const [, sendOTP] = useMutation(SEND_OTP);
  const [, verifyOTP] = useMutation(VERIFY_OTP);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otpState, setOTPState] = useState<"SEND" | "VERIFY">("SEND");
  const [otp, setOtp] = useState("");

  const handleGetOTP = async () => {
    setLoading(true);
    const { data, error } = await sendOTP({
      email: userEmail,
      appType: "booking",
    });

    setLoading(false);

    if (error) {
      console.error(error);
      setError(error.message);
      return;
    }

    if (data.otp?.email) {
      setOTPState("VERIFY");
    }
  };

  const handleVerifyOTP = async () => {
    setLoading(true);
    const { data, error } = await verifyOTP({
      email: userEmail,
      otp: otp,
    });

    setLoading(false);

    if (error) {
      console.error(error);
      setError(error.message);
      return;
    }

    if (data?.otp?.access_token) {
      onSuccess(data.otp.access_token, userCredits, authType);
      onClose();
    }
  };

  const handleCancel = () => {
    setOTPState("SEND");
    setOtp("");
    setError(null);
    onClose();
  };

  const okText =
    authType === "email"
      ? `${otpState === "SEND" ? "Get" : "Verify"} OTP`
      : "Verify Email";

  return (
    <Modal
      title="Login to use credits"
      visible={visible}
      onCancel={handleCancel}
      okText={okText}
      okButtonProps={{
        loading,
      }}
      onOk={otpState === "SEND" ? handleGetOTP : handleVerifyOTP}
    >
      <p>
        You have ${userCredits} credits available. Verify your email to use
        them.
      </p>
      {otpState === "VERIFY" && (
        <div>
          <p id="otp-label">
            Enter the OTP sent to <strong>{userEmail}</strong>
          </p>
          <FormInput
            type="tel"
            id="otp"
            aria-labelledby="otp-label"
            placeholder="Enter OTP"
            size="large"
            onChange={(e) => setOtp(e.target.value)}
          />
          {error && (
            <small style={{ color: "red" }}>
              {error}
              {error?.includes("expired") && (
                <button
                  onClick={handleGetOTP}
                  style={{
                    all: "unset",
                    marginLeft: 5,
                    color: "var(--blue-300)",
                    fontWeight: "semibold",
                    textDecoration: "underline",
                  }}
                >
                  Retry
                </button>
              )}
            </small>
          )}
        </div>
      )}
    </Modal>
  );
};

